import AddIcon from "@mui/icons-material/Add";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {UserType} from "crqlar-react-common/dist/models/identity/user.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../layout/rightSidebar/RightSidebarRail";
import {CreateRestaurantDialog} from "./CreateRestaurantDialog";

interface RestaurantOption {
    id: string;
    label: string;
    color?: string;
}

export interface SingleRestaurantResourcePickerProps {
    restaurants: readonly Resource[];
    selectedRestaurantId?: string;
    onSelectedRestaurantIdChange: (restaurantId: string) => void;
    onOpenRestaurantInNewTab?: (restaurantId: string) => void;
}

const selectableRowSx = (selected: boolean) => ({
    m: 0,
    px: 0.75,
    py: 0.5,
    width: "100%",
    minWidth: 0,
    borderRadius: 1,
    bgcolor: selected ? "action.selected" : "transparent",
    "&:hover": {bgcolor: "action.hover"},
    "& .MuiFormControlLabel-label": {flex: 1, minWidth: 0},
});

export function SingleRestaurantResourcePicker({
    restaurants,
    selectedRestaurantId,
    onSelectedRestaurantIdChange,
    onOpenRestaurantInNewTab,
}: SingleRestaurantResourcePickerProps) {
    const {t, i18n} = useTranslation();
    const readOnly = useReadOnly();
    const [createOpen, setCreateOpen] = useState(false);
    const myAccount = useQuery(["myAccount"], () => MyAccountService.getMyAccount());
    const canCreateRestaurant = !readOnly && [
        UserType.SUPER_ADMIN,
        UserType.GROUP_MANAGER,
        UserType.HOTEL_MANAGER,
        UserType.RESTAURANT_MODULE_MANAGER,
    ].includes(myAccount.data?.userType as UserType);
    const locale = i18n.resolvedLanguage || i18n.language;
    const options = useMemo<RestaurantOption[]>(() => {
        const collator = new Intl.Collator(locale, {numeric: true, sensitivity: "base"});

        return restaurants
            .filter((restaurant): restaurant is Resource & {id: string} => Boolean(restaurant.id))
            .map(restaurant => ({
                id: restaurant.id,
                label: getLocalizedValue(locale, restaurant.nameLocalized) || restaurant.name || t("unnamed"),
                color: restaurant.meta?.tagColor,
            }))
            .sort((left, right) => collator.compare(left.label, right.label));
    }, [locale, restaurants, t]);
    const selectedRestaurant = options.find(option => option.id === selectedRestaurantId);

    return (
        <>
            <RightSidebarPanelButton
                icon={<RestaurantMenuIcon/>}
                label={selectedRestaurant?.label || t("Select restaurant")}
                renderPanel={close => (
                    <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                        <RightSidebarPanelHeader title={t("Select restaurant")} onClose={close}/>

                        {canCreateRestaurant && (
                            <Stack direction="row" alignItems="center" sx={{px: 2, pb: 1.25}}>
                                <Button
                                    color="info"
                                    startIcon={<AddIcon/>}
                                    onClick={() => {
                                        setCreateOpen(true);
                                        close();
                                    }}
                                    sx={{whiteSpace: "nowrap", flexShrink: 0}}
                                >
                                    {t("Create New")}
                                </Button>
                            </Stack>
                        )}

                        <Stack sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2}}>
                            {options.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    {t("No restaurants available")}
                                </Typography>
                            ) : (
                                <RadioGroup
                                    value={selectedRestaurantId || ""}
                                    aria-label={t("Select restaurant")}
                                    onChange={event => {
                                        onSelectedRestaurantIdChange(event.target.value);
                                        close();
                                    }}
                                >
                                    {options.map(option => {
                                        const selected = option.id === selectedRestaurantId;

                                        return (
                                            <FormControlLabel
                                                key={option.id}
                                                value={option.id}
                                                control={<Radio size="small" sx={{p: 0.5, mr: 0.5}}/>}
                                                label={(
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={1}
                                                        sx={{width: "100%", minWidth: 0, pr: 1}}
                                                    >
                                                        <Typography noWrap>{option.label}</Typography>
                                                        <Box
                                                            aria-hidden
                                                            sx={{
                                                                width: 14,
                                                                height: 14,
                                                                flexShrink: 0,
                                                                borderRadius: "50%",
                                                                bgcolor: option.color || "action.disabledBackground",
                                                            }}
                                                        />
                                                    </Stack>
                                                )}
                                                onMouseDown={event => {
                                                    if (event.button !== 1 || !onOpenRestaurantInNewTab) return;

                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    onOpenRestaurantInNewTab(option.id);
                                                }}
                                                onAuxClick={event => {
                                                    if (event.button !== 1 || !onOpenRestaurantInNewTab) return;

                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                }}
                                                onClickCapture={event => {
                                                    const isMiddleButton = event.button === 1;
                                                    const isModifiedPrimaryButton = event.button === 0
                                                        && (event.metaKey || event.ctrlKey);
                                                    if (
                                                        (!isMiddleButton && !isModifiedPrimaryButton)
                                                        || !onOpenRestaurantInNewTab
                                                    ) return;

                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    if (isModifiedPrimaryButton) {
                                                        onOpenRestaurantInNewTab(option.id);
                                                    }
                                                }}
                                                sx={selectableRowSx(selected)}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            )}
                        </Stack>
                    </RightSidebarFloatingPanel>
                )}
            />

            <CreateRestaurantDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={restaurantId => onSelectedRestaurantIdChange(restaurantId)}
            />
        </>
    );
}

export default SingleRestaurantResourcePicker;
